import {
  enteredQuantities,
  remainingAfterEntry,
  addedAfterEntry,
  buildBatchesFromValues,
  FULFILL_ROW_LAYOUT,
} from '@/components/common';
// Cross-slice on purpose: a fulfilment draws from declarations, and both forms pick them alike.
import { SELECT_INBOUND_CONFIG } from '@/slices/declarations/constants/selectInboundConfig';
import { SELECT_OUTGOING_SHIPMENT_CONFIG } from './selectOutgoingShipmentConfig';
import { SHIPMENT_OPTION, resolveAssignedQuantity } from './fulfillOrder.constants';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { formatQuantityOrDash } from '@/lib/format';

// Orders ▸ Fulfil — what <FulfillForm> needs to draw this slice's version.

const buildGroups = (t, order, values) => {
  const groups = [
    {
      heading: t('SECTION_GENERAL_DETAILS'),
      fields: [
        { label: t('FIELD_MATERIAL_NAME'), value: order.materialName },
        { label: t('FIELD_SOLD_TO'), value: order.soldTo },
        { label: t('FIELD_SHIPPING_TO'), value: order.shippingTo },
        { label: t('FIELD_TRANSACTION_DATE'), value: order.transactionDate },
        { label: t('FIELD_DISPATCH_BY'), value: order.dispatchBy },
        { label: t('ORDERS_COL_TOLERANCE'), value: order.tolerance },
      ],
    },
  ];

  // Exactly one quantity row (working first, else declaration) — the VM sets no unitType to key off.
  const unitType = order.byUnit?.working ? UNIT_TYPES.working : UNIT_TYPES.declaration;
  const primaryQuantities = order.byUnit?.[unitType];
  if (primaryQuantities) {
    const unit = primaryQuantities.unit;
    const remaining = order.remainingByUnit?.[unit];
    const committed = order.committedByUnit?.[unit];
    const entered = enteredQuantities({
      unit,
      unitType,
      values,
      assigned: resolveAssignedQuantity(values),
    });
    groups.push({
      heading: t('SECTION_QUANTITY_IN_UNIT', { unit }),
      fields: [
        { label: t('FIELD_CONTRACTED_QUANTITY'), value: primaryQuantities.contractedQuantity },
        {
          label: t('FIELD_SHIPPED_QUANTITY'),
          value: formatQuantityOrDash(addedAfterEntry(committed?.shipped, entered.assigned), unit),
        },
        {
          label: t('ORDERS_COL_REMAINING_TO_SHIP'),
          value: formatQuantityOrDash(
            remainingAfterEntry(remaining?.toShip, entered.assigned),
            unit,
          ),
        },
        {
          label: t('FIELD_ALLOCATED_QUANTITY'),
          value: formatQuantityOrDash(
            addedAfterEntry(committed?.allocated, entered.allocated),
            unit,
          ),
        },
        {
          label: t('ORDERS_COL_REMAINING_TO_ALLOCATE'),
          value: formatQuantityOrDash(
            remainingAfterEntry(remaining?.toAllocate, entered.allocated),
            unit,
          ),
        },
      ],
    });
  }

  groups.push({
    heading: t('SECTION_PREFERENCES'),
    fields: [
      { label: t('ORDERS_COL_MAX_GHG_EMISSIONS'), value: order.maxGhgEmissions },
      { label: t('ORDERS_COL_MIN_GHG_SAVINGS'), value: order.minGhgSavings },
      { label: t('FIELD_RAW_MATERIAL_TYPE'), value: order.rawMaterialType },
      { label: t('FIELD_COUNTRY_OF_ORIGIN'), value: order.countryOfOrigin },
      { label: t('FIELD_FEEDSTOCK_CATEGORY'), value: order.feedstockCategory },
      { label: t('FIELD_ALLOCATION_PREFERENCE'), value: order.allocationPreference },
    ],
  });

  return groups;
};

const TILE_FIELDS = Object.freeze([
  { labelKey: 'ORDERS_FULFILL_CONTRACTED_QUANTITY_TILE', amount: 'contractedQuantity' },
  {
    labelKey: 'ORDERS_FULFILL_REMAINING_TO_SHIP_TILE',
    amount: 'remainingToShipQuantity',
    remaining: 'toShip',
    entered: 'assigned',
  },
  {
    labelKey: 'ORDERS_FULFILL_REMAINING_TO_ALLOCATE_TILE',
    amount: 'remainingToAllocateQuantity',
    remaining: 'toAllocate',
    entered: 'allocated',
  },
]);

export const FULFILL_ORDER_CONFIG = Object.freeze({
  buildGroups,
  noteKey: 'ORDERS_FULFILL_REQUIRED_FIELDS_NOTE',
  // Select inbound ends where Raw material type does in the summary card above.
  alignToSummary: true,
  // Three branches off one question, so the radio and each branch are their own section.
  sections: [
    {
      name: 'allocations',
      sectionHeadingKey: 'ORDERS_FULFILL_SECTION_ALLOCATIONS',
      rows: [{ slot: 'allocations', columns: 3, layout: FULFILL_ROW_LAYOUT.summaryAnchoredHalves }],
    },
    {
      // One block: the question, whichever fields its answer calls for, and the picker.
      ...SELECT_OUTGOING_SHIPMENT_CONFIG,
      name: 'outgoingShipment',
      sectionHeadingKey: 'ORDERS_FULFILL_SECTION_OUTGOING_SHIPMENT',
      pickerVisible: (values) => values.shipmentOption === SHIPMENT_OPTION.selectExisting,
      rows: [
        { slot: 'shipmentOption', layout: FULFILL_ROW_LAYOUT.summaryAnchored },
        {
          slot: 'createNewShipment',
          columns: 4,
          isVisible: (values) => values.shipmentOption === SHIPMENT_OPTION.createNew,
        },
      ],
    },
    {
      ...SELECT_INBOUND_CONFIG,
      tileFields: TILE_FIELDS,
      // An order fulfils itself, so the inbound picker matches against this very order.
      readPendingOrderId: (values, record) => record?.id,
      // Create new types its size as the shipment quantity, so the tiles must read it there.
      readAssigned: (values) => resolveAssignedQuantity(values),
    },
  ],
  required: [
    { name: 'selectInbound', isMissing: (values) => buildBatchesFromValues(values).length === 0 },
  ],
});
